import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  Row,
  Spinner,
  InputGroup,
  ProgressBar,
  Carousel,
} from "react-bootstrap";

/**
 * DISCOUNT_JEWELRY_Landing_v01A_WIZARD (React + TypeScript + React-Bootstrap)
 * Version: v01A
 * Date: 2025-08-30 (America/Denver)
 *
 * Highlights (keeps your framework):
 * - Wizard/Vertical toggle with progress.
 * - On-blur validation (vp/vpOpt) + subtle green background when valid.
 * - Modular submit pipeline: buildLeadPayload -> toWirePayloadStrings -> postLead.
 * - Section components reused across Wizard & Vertical (no duplicated JSX).
 * - Sectioned confirmation summary (readable; no raw JSON).
 */

/* ============================================================
   CONFIG
============================================================ */
const VERSION = "v01A" as const;
const SHOW_VERSION = false;
const TEST_MODE = false; // true => logs payload, skips network
const API_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback";
const AGENT_PHONE_NUMBER = "(303) 337-4433";
const imagePath = "insurance/images/JEWELRY-Insurance/"; // adjust to your asset folder

/* ============================================================
   TYPES
============================================================ */

type CoverageType = "scheduled" | "blanket";
type Deductible = "" | "0" | "100" | "250" | "500" | "1000";

type JewelryType =
  | "ring"
  | "necklace"
  | "bracelet"
  | "earrings"
  | "watch"
  | "loose_stone"
  | "collection"
  | "other";

interface IJewelryLeadPayload {
  // Contact
  fullName: string;
  email: string;
  phone: string; // digits only 10

  // Address
  address1: string;
  address2?: string;
  city: string;
  state: string; // 2-letter
  zipCode: string; // 5-digit

  // Jewelry details
  jewelryType: JewelryType;
  description: string; // brief description or brand/model
  primaryMetal?: string; // gold/platinum/silver/etc
  gemstones?: string; // diamond/sapphire/etc
  appraisedValue: string; // numeric string USD
  haveAppraisal: boolean;
  appraisalDate?: string; // yyyy-mm-dd optional
  purchaseDate?: string; // yyyy-mm-dd optional

  // Coverage & security
  coverageType: CoverageType;
  scheduleItemCount?: string; // numeric string
  deductible: Deductible;
  storedInSafe: boolean;
  safeIsRated: boolean;
  monitoredAlarm: boolean;
  priorLosses5y: string; // numeric string
  lastLossDate?: string; // yyyy-mm-dd optional

  // Notes / Files
  notes?: string;
  photoUrls?: string; // newline- or comma-separated

  // Meta
  domainName: string;
  itemCategory: string; // Customer.jewelry.quote
  itemClass: string; // quote.request
  itemType: string; // insurance.jewelry.landing
  clientVersion: string;
  utm?: { source?: string; medium?: string; campaign?: string };
}

/** Wire payload where EVERY VALUE IS A STRING */
type WirePayload = Record<string, string>;

/* ============================================================
   HELPERS
============================================================ */
const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

function digitsOnly(s: string): string { return (s ?? "").replace(/\D+/g, ""); }
function isValidEmail(email: string): boolean { return /.+@.+\..+/.test(email); }
function isValidPhone(raw: string): boolean { return digitsOnly(raw).length === 10; }
function isValidZipCode(raw: string): boolean { return digitsOnly(raw).length === 5; }
function formatPhonePretty(raw: string): string {
  const d = digitsOnly(raw);
  if (d.length !== 10) return raw || "—";
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
const dash = (v?: string | number | boolean) => (v === undefined || v === null || (typeof v === "string" && v.trim().length === 0)) ? "—" : String(v);
const money = (nLike?: string) => {
  const digits = (nLike ?? "").replace(/[^\d.]/g, "");
  const n = Number(digits || "0");
  if (!Number.isFinite(n) || n <= 0) return "—";
  try { return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }); }
  catch { return `$${n}`; }
};

/* ============================================================
   SLIDES
============================================================ */
interface Slide { src: string; alt: string; title: string; subline: string; smallCaption: string; }
const slides: Slide[] = [
  {
    src: imagePath + "AdobeStock_143089356_JewelryCase_Preview.jpg",
    alt: "Jewelry case with rings and necklaces.",
    title: "Discount Jewelry Insurance",
    subline: "Protect your rings, watches, and heirlooms—at great rates.",
    smallCaption: "Fast quotes. Broad coverage.",
  },
  {
    src: imagePath + "AdobeStock_196540879_DiamondRing_Preview.jpg",
    alt: "Close-up of diamond ring.",
    title: "Scheduled or Blanket Coverage",
    subline: "Insure one piece or your whole collection.",
    smallCaption: "Flexible options.",
  },
  {
    src: imagePath + "AdobeStock_248021708_Watch_Preview.jpg",
    alt: "Luxury watch on wrist.",
    title: "Theft, Loss & Mysterious Disappearance",
    subline: "Specialized coverage that homeowners policies often miss.",
    smallCaption: "Peace of mind.",
  },
];

// Q&A
export interface JewelryInsuranceFAQ {
  id: string;
  question: string;
  answer: string;
}

export const jewelryInsuranceFAQs: JewelryInsuranceFAQ[] = [
  {
    id: "q1",
    question: "What is jewelry insurance?",
    answer: "Jewelry insurance protects against risks like theft, loss, damage, or mysterious disappearance of valuable items such as rings, watches, or necklaces."
  },
  {
    id: "q2",
    question: "Does homeowners or renters insurance cover jewelry?",
    answer: "Most homeowners and renters policies only cover jewelry up to a small limit, often $1,500. A separate jewelry policy provides broader protection."
  },
  {
    id: "q3",
    question: "How is jewelry insurance priced?",
    answer: "Rates are usually based on the appraised value, type of jewelry, where you live, and how the piece is worn or stored. Expect about 1–2% of value per year."
  },
  {
    id: "q4",
    question: "Do I need an appraisal?",
    answer: "Yes, most carriers require an appraisal or detailed sales receipt to establish replacement value for coverage."
  },
  {
    id: "q5",
    question: "What types of jewelry can be insured?",
    answer: "Engagement rings, wedding bands, watches, earrings, bracelets, heirlooms, loose stones, and even collections can be covered."
  },
  {
    id: "q6",
    question: "Am I covered if I lose my ring?",
    answer: "Yes, jewelry insurance often covers accidental loss and mysterious disappearance—unlike standard homeowners policies."
  },
  {
    id: "q7",
    question: "Does insurance cover damage?",
    answer: "Yes, accidental damage like a chipped stone, bent prong, or broken clasp is typically included."
  },
  {
    id: "q8",
    question: "Are travel losses covered?",
    answer: "Yes, most jewelry policies include worldwide coverage for loss, theft, or damage while traveling."
  },
  {
    id: "q9",
    question: "Can I insure a jewelry collection?",
    answer: "Yes, you can schedule individual items or insure your collection as a whole with blanket coverage."
  },
  {
    id: "q10",
    question: "What about antique or heirloom jewelry?",
    answer: "Antiques and heirlooms can be insured, but insurers usually require an up-to-date appraisal for valuation."
  },
  {
    id: "q11",
    question: "What’s mysterious disappearance coverage?",
    answer: "It covers situations where jewelry goes missing without clear evidence of theft—like losing an earring at the gym."
  },
  {
    id: "q12",
    question: "Do I have to replace jewelry with the same item?",
    answer: "Policies generally pay for repair, replacement with like-kind quality, or cash settlement, depending on carrier and choice."
  },
  {
    id: "q13",
    question: "Can I insure jewelry bought online?",
    answer: "Yes—submit the purchase receipt and, if required, an appraisal once received to establish coverage."
  },
  {
    id: "q14",
    question: "How quickly can coverage start?",
    answer: "Often the same day your application and appraisal are submitted, with proof of insurance available immediately."
  },
  {
    id: "q15",
    question: "Does my deductible affect the premium?",
    answer: "Yes, higher deductibles lower premiums. Some policies even offer zero-deductible options."
  },
  {
    id: "q16",
    question: "What if my jewelry appreciates in value?",
    answer: "Appraisals should be updated every 2–3 years to ensure your policy reflects current replacement cost."
  },
  {
    id: "q17",
    question: "Are loose stones covered?",
    answer: "Yes, as long as they are itemized and appraised. Coverage applies whether mounted or awaiting setting."
  },
  {
    id: "q18",
    question: "Can I add new jewelry to my policy?",
    answer: "Yes, most policies allow you to add newly purchased items within 30–90 days with temporary automatic coverage."
  },
  {
    id: "q19",
    question: "Does insurance cover wear and tear?",
    answer: "No, normal wear, scratches, or gradual deterioration are excluded. Coverage applies to sudden loss or damage."
  },
  {
    id: "q20",
    question: "Do jewelers need different coverage?",
    answer: "Yes, jewelers and jewelry businesses usually need Jewelers Block or Business Owner’s policies. Personal jewelry insurance is for individuals."
  }
];





/* ============================================================
   MAIN
============================================================ */
const steps = ["Contact & Address", "Jewelry Details", "Coverage & Security", "Confirmation"] as const;

export const DISCOUNT_JEWELRY_Landing_v020_WIZARD: React.FC = () => {
  const formRef = useRef<HTMLDivElement | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Contact
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // Address
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");

  // Jewelry details
  const [jewelryType, setJewelryType] = useState<JewelryType>("ring");
  const [description, setDescription] = useState<string>("");
  const [primaryMetal, setPrimaryMetal] = useState<string>("");
  const [gemstones, setGemstones] = useState<string>("");
  const [appraisedValue, setAppraisedValue] = useState<string>("");
  const [haveAppraisal, setHaveAppraisal] = useState<boolean>(false);
  const [appraisalDate, setAppraisalDate] = useState<string>("");
  const [purchaseDate, setPurchaseDate] = useState<string>("");

  // Coverage & security
  const [coverageType, setCoverageType] = useState<CoverageType>("scheduled");
  const [scheduleItemCount, setScheduleItemCount] = useState<string>("1");
  const [deductible, setDeductible] = useState<Deductible>("250");
  const [storedInSafe, setStoredInSafe] = useState<boolean>(false);
  const [safeIsRated, setSafeIsRated] = useState<boolean>(false);
  const [monitoredAlarm, setMonitoredAlarm] = useState<boolean>(false);
  const [priorLosses5y, setPriorLosses5y] = useState<string>("0");
  const [lastLossDate, setLastLossDate] = useState<string>("");

  // Notes / Files
  const [notes, setNotes] = useState<string>("");
  const [photoUrls, setPhotoUrls] = useState<string>("");

  // Submit / server state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);
  const [serverStatus, setServerStatus] = useState<null | { ok: boolean; status?: number; id?: string; message?: string }>(null);
  const [lastSubmittedApp, setLastSubmittedApp] = useState<IJewelryLeadPayload | null>(null);

  // Mode + wizard
  const [mode, setMode] = useState<"wizard" | "vertical">("wizard");
  const isWizard = mode === "wizard";
  const [currentStep, setCurrentStep] = useState(0);

  /* ---------- Touched + validation ---------- */
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const isTouched = (name: string) => !!touched[name];
  const markTouched = (name: string) => setTouched((t) => ({ ...t, [name]: true }));

  const vp = (name: string, pass: boolean) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && pass,
    isInvalid: isTouched(name) && !pass,
    className: isTouched(name) && pass ? "bg-success-subtle" : undefined,
  });
  const vpOpt = (name: string, valuePresent: boolean, passIfPresent: boolean = true) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && valuePresent && passIfPresent,
    isInvalid: isTouched(name) && valuePresent && !passIfPresent,
    className: isTouched(name) && valuePresent && passIfPresent ? "bg-success-subtle" : undefined,
  });

  // Validators
  const V = {
    fullName: fullName.trim().length >= 2,
    email: isValidEmail(email),
    phone: isValidPhone(phone),

    address1: address1.trim().length >= 3,
    city: city.trim().length >= 2,
    state: state !== "",
    zip: isValidZipCode(zipCode),

    description: description.trim().length >= 2,
    appraisedValue: Number(digitsOnly(appraisedValue)) > 0,
    appraisalDate: !haveAppraisal || !appraisalDate || /^\d{4}-\d{2}-\d{2}$/.test(appraisalDate),
    purchaseDate: !purchaseDate || /^\d{4}-\d{2}-\d{2}$/.test(purchaseDate),

    coverageType: coverageType === "scheduled" || coverageType === "blanket",
    scheduleItemCount: coverageType === "scheduled" ? Number(digitsOnly(scheduleItemCount)) >= 1 : true,
    deductible: deductible !== "",
    priorLosses5y: Number(digitsOnly(priorLosses5y)) >= 0 && Number(digitsOnly(priorLosses5y)) <= 10,
    lastLossDate: !lastLossDate || /^\d{4}-\d{2}-\d{2}$/.test(lastLossDate),

    notes: notes.trim().length === 0 || notes.trim().length >= 2,
    photoUrls: photoUrls.trim().length === 0 || photoUrls.trim().length >= 5,
  };

  const valid = useMemo(() => (
    V.fullName && V.email && V.phone && V.address1 && V.city && V.state && V.zip &&
    V.description && V.appraisedValue && V.appraisalDate && V.purchaseDate &&
    V.coverageType && V.scheduleItemCount && V.deductible && V.priorLosses5y && V.lastLossDate &&
    V.notes && V.photoUrls
  ), [V]);

  function isStepValid(stepIdx: number): boolean {
    switch (stepIdx) {
      case 0: // Contact & Address
        return V.fullName && V.email && V.phone && V.address1 && V.city && V.state && V.zip;
      case 1: // Jewelry Details
        return V.description && V.appraisedValue && V.appraisalDate && V.purchaseDate;
      case 2: // Coverage & Security
        return V.coverageType && V.scheduleItemCount && V.deductible && V.priorLosses5y && V.lastLossDate && V.notes && V.photoUrls;
      case 3:
        return submitOk === true;
      default:
        return false;
    }
  }

  const goNext = () => {
    const touchMany = (names: string[]) => setTouched((t) => names.reduce((acc, n) => ({ ...acc, [n]: true }), t));
    if (currentStep === 0) touchMany(["fullName","email","phone","address1","city","state","zip"]);
    else if (currentStep === 1) touchMany(["description","appraisedValue","appraisalDate","purchaseDate"]);
    else if (currentStep === 2) touchMany(["coverageType","scheduleItemCount","deductible","priorLosses5y","lastLossDate","notes","photoUrls"]);
    if (!isStepValid(currentStep)) return;
    setCurrentStep((s) => Math.min(3, s + 1));
  };
  const goBack = () => setCurrentStep((s) => Math.max(0, s - 1));

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  /* ============================================================
     PAYLOAD & SUBMIT
  ============================================================ */
  function buildLeadPayload(): IJewelryLeadPayload {
    return {
      // Contact
      fullName: fullName.trim(),
      email: email.trim(),
      phone: digitsOnly(phone),

      // Address
      address1: address1.trim(),
      address2: address2?.trim() || "",
      city: city.trim(),
      state: String(state),
      zipCode: String(zipCode),

      // Jewelry
      jewelryType,
      description: description.trim(),
      primaryMetal: primaryMetal?.trim() || "",
      gemstones: gemstones?.trim() || "",
      appraisedValue: String(Number(digitsOnly(appraisedValue))),
      haveAppraisal,
      appraisalDate: appraisalDate || "",
      purchaseDate: purchaseDate || "",

      // Coverage & security
      coverageType,
      scheduleItemCount: coverageType === "scheduled" ? String(Number(digitsOnly(scheduleItemCount))) : "",
      deductible,
      storedInSafe,
      safeIsRated,
      monitoredAlarm,
      priorLosses5y: String(Number(digitsOnly(priorLosses5y))),
      lastLossDate: lastLossDate || "",

      // Notes / Files
      notes: notes?.trim() || "",
      photoUrls: photoUrls?.trim() || "",

      // Meta
      domainName: typeof window !== "undefined" ? String(window.location.hostname) : "unknown",
      itemCategory: "Customer.jewelry.quote",
      itemClass: "quote.request",
      itemType: "insurance.jewelry.landing",
      clientVersion: String(VERSION),
    };
  }

  function toWirePayloadStrings(p: IJewelryLeadPayload): WirePayload {
    const out: WirePayload = {};
    for (const [k, v] of Object.entries(p)) {
      if (typeof v === "string") out[k] = v;
      else if (typeof v === "number") out[k] = Number.isFinite(v) ? `${v}` : "";
      else if (typeof v === "boolean") out[k] = v ? "true" : "false";
      else if (Array.isArray(v)) { try { out[k] = JSON.stringify(v); } catch { out[k] = ""; } }
      else if (v && typeof v === "object") { try { out[k] = JSON.stringify(v); } catch { out[k] = ""; } }
      else out[k] = "";
    }
    return out;
  }

  async function postLead(payload: WirePayload): Promise<{ ok: boolean; status: number; id?: string; message?: string }> {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      mode: "cors",
    });

    let resJson: unknown = null;
    try { const txt = await res.text(); resJson = txt ? JSON.parse(txt) : null; } catch { resJson = null; }
    const asObj = (x: unknown): Record<string, unknown> | null => x && typeof x === "object" && !Array.isArray(x) ? (x as Record<string, unknown>) : null;

    if (!res.ok) {
      const o = asObj(resJson); const msg = (o?.error as string) || (o?.message as string) || `Request failed: ${res.status}`;
      return { ok: false, status: res.status, message: msg };
    }
    const o = asObj(resJson); const id = (o?.id as string) || (o?.requestId as string) || undefined;
    return { ok: true, status: res.status, id };
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    // Only submit on last data step in Wizard; otherwise advance.
    const lastDataStepIdx = 2;
    if (isWizard && currentStep < lastDataStepIdx) { goNext(); return; }

    setSubmitError(null); setSubmitOk(false); setServerStatus(null);

    // Mark required fields as touched for final validation
    setTouched((t) => ({
      ...t,
      fullName: true, email: true, phone: true,
      address1: true, city: true, state: true, zip: true,
      description: true, appraisedValue: true, appraisalDate: true, purchaseDate: true,
      coverageType: true, scheduleItemCount: true, deductible: true, priorLosses5y: true, lastLossDate: true,
      notes: true, photoUrls: true,
    }));

    if (!valid) { setSubmitError("Please complete all required fields."); return; }

    const appPayload = buildLeadPayload();
    const wirePayload = toWirePayloadStrings(appPayload);

    try {
      setSubmitting(true);
      if (TEST_MODE) {
        console.log("[TEST_MODE] Submitting payload (wire):", wirePayload);
        await new Promise((r) => setTimeout(r, 600));
        const status = { ok: true, status: 200, id: "TEST-" + Math.random().toString(36).slice(2, 8) };
        setServerStatus(status); setLastSubmittedApp(appPayload); setSubmitOk(true);
        if (isWizard) setCurrentStep(3);
        return;
      }

      const status = await postLead(wirePayload);
      setServerStatus(status);
      if (!status.ok) throw new Error(status.message || "Unknown server error");
      setLastSubmittedApp(appPayload); setSubmitOk(true);
      if (isWizard) setCurrentStep(3);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSubmitError(msg); setSubmitOk(false);
    } finally { setSubmitting(false); }
  };

  // Clamp active slide index
  const active = slides[Math.min(slides.length - 1, Math.max(0, Number.isFinite(activeSlide) ? activeSlide : 0))];

  /* ------------------------------------------------------------
     UI
  ------------------------------------------------------------ */
  return (
    <div className="bg-body">
      {/* Sticky Topbar */}
      <Navbar expand="md" bg="light" className="border-bottom sticky-top">
        <Container>
          <Navbar.Brand href="#" className="fw-bold">
            Discount <span className="text-primary">Jewelry</span> Insurance
            {SHOW_VERSION && <Badge bg="secondary" className="ms-2">{VERSION}</Badge>}
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="topbar" />
          <Navbar.Collapse id="topbar" className="justify-content-end">
            <Nav className="align-items-center gap-2">
              <div className="d-none d-md-block text-body-secondary me-2">Speak with an advisor:</div>
              <Button variant="outline-primary" href="tel:3033374433">{AGENT_PHONE_NUMBER}</Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO */}
      <section id="home" className="position-relative overflow-hidden" style={{ minHeight: "760px" }}>
        <div className="position-absolute top-0 start-0 end-0 bottom-0" style={{ zIndex: 0 }} aria-hidden>
          <Carousel
            activeIndex={activeSlide}
            onSelect={(i) => setActiveSlide(typeof i === "number" ? i : 0)}
            controls
            indicators
            fade
            interval={3500}
            className="w-100 h-100"
          >
            {slides.map((s, idx) => (
              <Carousel.Item key={idx} className="w-100 h-100">
                <div
                  role="img"
                  aria-label={s.alt}
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 760,
                    backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.55), rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.10) 60%, rgba(0,0,0,0.00)), url('${s.src}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <Carousel.Caption className="text-start" style={{ bottom: "40%" }} />
              </Carousel.Item>
            ))}
          </Carousel>
        </div>

        {/* Foreground content */}
        <Container className="position-relative" style={{ zIndex: 1 }}>
          <Row className="align-items-start g-4 p-5">
            <Col lg={6} className="text-white">
              <div className="pe-lg-4">
                <h1 className="display-5 fw-bold" style={{ marginTop: "400px" }}>{active.title}</h1>
                <p className="lead mb-3">{active.subline}</p>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Badge bg="light" text="dark">Worldwide Travel Options</Badge>
                  <Badge bg="light" text="dark">Mysterious Disappearance</Badge>
                  <Badge bg="light" text="dark">Licensed Advisors</Badge>
                </div>
                <div className="mt-4 d-flex gap-2">
                  <Button variant="outline-light" onClick={scrollToForm}>Start My Quote</Button>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              <Card className="shadow-lg" style={{ backdropFilter: "blur(6px)" }}>
                <Card.Header className="bg-white">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="fw-semibold">Get Your Jewelry Insurance Quote</div>
                      <small className="text-body-secondary">Schedule individual items or cover a collection.</small>
                    </div>
                    <Badge bg="success">Secure</Badge>
                  </div>
                </Card.Header>
                <Card.Body ref={formRef}>
                  {submitError && (
                    <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>
                      Server: {submitError}
                    </Alert>
                  )}

                  {/* MODE TOGGLE + (Wizard) Stepper */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="switch"
                        id="modeSwitch"
                        label={isWizard ? "Wizard mode" : "Vertical mode"}
                        checked={isWizard}
                        onChange={(e) => {
                          setMode(e.currentTarget.checked ? "wizard" : "vertical");
                          setTouched({});
                          if (submitOk && e.currentTarget.checked) setCurrentStep(3); else setCurrentStep(0);
                        }}
                      />
                      <small className="text-muted">Switch to see all fields in one view.</small>
                    </div>

                    <div style={{ minWidth: 280 }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Badge bg="secondary">Step {isWizard ? currentStep + 1 : (submitOk ? 2 : 1)} of {isWizard ? 4 : (submitOk ? 2 : 1)}</Badge>
                        <small className="text-muted">{isWizard ? steps[currentStep] : (submitOk ? "Confirmation" : "All fields")}</small>
                      </div>
                      <ProgressBar now={isWizard ? ((currentStep + 1) / 4) * 100 : (submitOk ? 100 : 50)} />
                    </div>
                  </div>

                  {/* FORM */}
                  <Form
                    onSubmit={handleSubmit}
                    noValidate
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && isWizard) {
                        if (currentStep < 2) { e.preventDefault(); e.stopPropagation(); if (isStepValid(currentStep)) goNext(); }
                      }
                    }}
                  >
                    {isWizard ? (
                      <>
                        <Row className="g-3">
                          {currentStep === 0 && (
                            <Col xs={12}>
                              <SectionCard title="Contact & Address">
                                <SectionContactAddress
                                  values={{ fullName, email, phone, address1, address2, city, state, zipCode }}
                                  setters={{ setFullName, setEmail, setPhone, setAddress1, setAddress2, setCity, setState, setZipCode }}
                                  V={V}
                                  vp={vp}
                                  vpOpt={vpOpt}
                                />
                              </SectionCard>
                            </Col>
                          )}

                          {currentStep === 1 && (
                            <Col xs={12}>
                              <SectionCard title="Jewelry Details">
                                <SectionJewelryDetails
                                  values={{ jewelryType, description, primaryMetal, gemstones, appraisedValue, haveAppraisal, appraisalDate, purchaseDate }}
                                  setters={{ setJewelryType, setDescription, setPrimaryMetal, setGemstones, setAppraisedValue, setHaveAppraisal, setAppraisalDate, setPurchaseDate }}
                                  V={V}
                                  vp={vp}
                                  vpOpt={vpOpt}
                                />
                              </SectionCard>
                            </Col>
                          )}

                          {currentStep === 2 && (
                            <Col xs={12}>
                              <SectionCard title="Coverage & Security">
                                <SectionCoverageSecurity
                                  values={{ coverageType, scheduleItemCount, deductible, storedInSafe, safeIsRated, monitoredAlarm, priorLosses5y, lastLossDate, notes, photoUrls }}
                                  setters={{ setCoverageType, setScheduleItemCount, setDeductible, setStoredInSafe, setSafeIsRated, setMonitoredAlarm, setPriorLosses5y, setLastLossDate, setNotes, setPhotoUrls }}
                                  V={V}
                                  vp={vp}
                                  vpOpt={vpOpt}
                                />
                              </SectionCard>
                            </Col>
                          )}

                          {currentStep === 3 && (
                            <Col xs={12}>
                              <ConfirmationBlock status={serverStatus} app={lastSubmittedApp} />
                            </Col>
                          )}
                        </Row>

                        {/* Wizard controls */}
                        {currentStep < 3 && (
                          <div className="d-flex justify-content-between mt-3">
                            <Button
                              type="button"
                              variant="outline-secondary"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); goBack(); }}
                              disabled={currentStep === 0}
                            >
                              Previous
                            </Button>

                            {currentStep < 2 ? (
                              <Button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (isStepValid(currentStep)) goNext(); }} disabled={!isStepValid(currentStep)}>
                                Next
                              </Button>
                            ) : (
                              <Button type="submit" variant="primary" size="lg" disabled={submitting || !valid}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                              </Button>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      // VERTICAL VIEW (reuse same sections)
                      <>
                        {!submitOk ? (
                          <>
                            <Row className="g-3">
                              <Col xs={12}>
                                <SectionCard title="Contact & Address">
                                  <SectionContactAddress
                                    values={{ fullName, email, phone, address1, address2, city, state, zipCode }}
                                    setters={{ setFullName, setEmail, setPhone, setAddress1, setAddress2, setCity, setState, setZipCode }}
                                    V={V}
                                    vp={vp}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>

                              <Col xs={12}>
                                <SectionCard title="Jewelry Details">
                                  <SectionJewelryDetails
                                    values={{ jewelryType, description, primaryMetal, gemstones, appraisedValue, haveAppraisal, appraisalDate, purchaseDate }}
                                    setters={{ setJewelryType, setDescription, setPrimaryMetal, setGemstones, setAppraisedValue, setHaveAppraisal, setAppraisalDate, setPurchaseDate }}
                                    V={V}
                                    vp={vp}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>

                              <Col xs={12}>
                                <SectionCard title="Coverage & Security">
                                  <SectionCoverageSecurity
                                    values={{ coverageType, scheduleItemCount, deductible, storedInSafe, safeIsRated, monitoredAlarm, priorLosses5y, lastLossDate, notes, photoUrls }}
                                    setters={{ setCoverageType, setScheduleItemCount, setDeductible, setStoredInSafe, setSafeIsRated, setMonitoredAlarm, setPriorLosses5y, setLastLossDate, setNotes, setPhotoUrls }}
                                    V={V}
                                    vp={vp}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>
                            </Row>

                            <div className="d-grid mt-3">
                              <Button type="submit" variant="primary" size="lg" disabled={!valid || submitting}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                              </Button>
                              <div className="text-center mt-2 text-body-secondary" style={{ fontSize: "0.9rem" }}>
                                By clicking, you agree we may contact you about jewelry insurance options. No spam. You can opt out anytime.
                              </div>
                            </div>
                          </>
                        ) : (
                          <ConfirmationBlock status={serverStatus} app={lastSubmittedApp} />
                        )}
                      </>
                    )}
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works */}
      <section className="py-5 bg-light border-top border-bottom">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">How It Works</h2>
              <p className="text-body-secondary mb-0">Three simple steps to protect your jewelry.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">1</div>
                <div>
                  <h6 className="mb-1">Tell us about you</h6>
                  <p className="mb-0 text-body-secondary">Contact, address, and basic details.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">2</div>
                <div>
                  <h6 className="mb-1">Describe your item(s)</h6>
                  <p className="mb-0 text-body-secondary">Type, materials, value, and appraisal info.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">3</div>
                <div>
                  <h6 className="mb-1">Choose coverage</h6>
                  <p className="mb-0 text-body-secondary">Scheduled/blanket, deductible, and security.
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="small text-body-secondary">
              © {new Date().getFullYear()} Discount Jewelry Insurance. All rights reserved.
              <div className="mt-2">Insurance services subject to licensing and state availability. This page is for general information and not legal advice.</div>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end mt-3 mt-md-0 gap-2">
              <Button variant="secondary" size="sm" href="tel:3033374433">Call {AGENT_PHONE_NUMBER}</Button>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

/* ============================================================
   REUSABLE SECTION COMPONENTS
============================================================ */

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <fieldset className="border rounded-3 p-3 bg-body-secondary">
    <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
      <strong>{title}</strong>
    </legend>
    <div className="mt-1">{children}</div>
  </fieldset>
);

/* ---------- Contact & Address ---------- */
const SectionContactAddress: React.FC<{
  values: { fullName: string; email: string; phone: string; address1: string; address2: string; city: string; state: string; zipCode: string };
  setters: { setFullName: (v: string) => void; setEmail: (v: string) => void; setPhone: (v: string) => void; setAddress1: (v: string) => void; setAddress2: (v: string) => void; setCity: (v: string) => void; setState: (v: string) => void; setZipCode: (v: string) => void };
  V: any;
  vp: (name: string, pass: boolean) => any;
  vpOpt: (name: string, valuePresent: boolean, passIfPresent?: boolean) => any;
}> = ({ values, setters, V, vp, vpOpt }) => {
  const { fullName, email, phone, address1, address2, city, state, zipCode } = values;
  const { setFullName, setEmail, setPhone, setAddress1, setAddress2, setCity, setState, setZipCode } = setters;

  return (
    <Row className="g-3">
      <Col md={6}>
        <Form.Label htmlFor="fullName">Full Name</Form.Label>
        <Form.Control id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jane Doe" required {...vp("fullName", V.fullName)} />
        <Form.Control.Feedback type="invalid">Please enter your full name.</Form.Control.Feedback>
      </Col>
      <Col md={3}>
        <Form.Label htmlFor="phone">Phone</Form.Label>
        <Form.Control id="phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" required {...vp("phone", V.phone)} />
        <Form.Control.Feedback type="invalid">Enter a 10-digit phone.</Form.Control.Feedback>
      </Col>
      <Col md={3}>
        <Form.Label htmlFor="email">Email</Form.Label>
        <Form.Control id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required {...vp("email", V.email)} />
        <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
      </Col>

      <Col md={8}>
        <Form.Label htmlFor="address1">Street Address</Form.Label>
        <Form.Control id="address1" value={address1} onChange={(e) => setAddress1(e.target.value)} placeholder="123 Main St" required {...vp("address1", V.address1)} />
        <Form.Control.Feedback type="invalid">Enter your street address.</Form.Control.Feedback>
      </Col>
      <Col md={4}>
        <Form.Label htmlFor="address2">Unit / Suite (optional)</Form.Label>
        <Form.Control id="address2" value={address2} onChange={(e) => setAddress2(e.target.value)} placeholder="Suite 200" {...vpOpt("address2", !!address2.trim())} />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="city">City</Form.Label>
        <Form.Control id="city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" required {...vp("city", V.city)} />
      </Col>
      <Col md={4}>
        <Form.Label htmlFor="state">State</Form.Label>
        <Form.Select id="state" value={state} onChange={(e) => setState(e.target.value)} required {...vp("state", V.state)}>
          <option value="">Select</option>
          {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
        </Form.Select>
      </Col>
      <Col md={4}>
        <Form.Label htmlFor="zipCode">Zip</Form.Label>
        <Form.Control id="zipCode" inputMode="numeric" value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="#####" required {...vp("zip", V.zip)} />
        <Form.Control.Feedback type="invalid">Enter a 5-digit zip code.</Form.Control.Feedback>
      </Col>
    </Row>
  );
};

/* ---------- Jewelry Details ---------- */
const SectionJewelryDetails: React.FC<{
  values: { jewelryType: JewelryType; description: string; primaryMetal: string; gemstones: string; appraisedValue: string; haveAppraisal: boolean; appraisalDate: string; purchaseDate: string };
  setters: { setJewelryType: (v: JewelryType) => void; setDescription: (v: string) => void; setPrimaryMetal: (v: string) => void; setGemstones: (v: string) => void; setAppraisedValue: (v: string) => void; setHaveAppraisal: (v: boolean) => void; setAppraisalDate: (v: string) => void; setPurchaseDate: (v: string) => void };
  V: any;
  vp: (name: string, pass: boolean) => any;
  vpOpt: (name: string, valuePresent: boolean, passIfPresent?: boolean) => any;
}> = ({ values, setters, V, vp, vpOpt }) => {
  const { jewelryType, description, primaryMetal, gemstones, appraisedValue, haveAppraisal, appraisalDate, purchaseDate } = values;
  const { setJewelryType, setDescription, setPrimaryMetal, setGemstones, setAppraisedValue, setHaveAppraisal, setAppraisalDate, setPurchaseDate } = setters;

  return (
    <Row className="g-3">
      <Col md={4}>
        <Form.Label htmlFor="jType">Jewelry Type</Form.Label>
        <Form.Select id="jType" value={jewelryType} onChange={(e) => setJewelryType(e.target.value as JewelryType)}>
          <option value="ring">Ring</option>
          <option value="necklace">Necklace</option>
          <option value="bracelet">Bracelet</option>
          <option value="earrings">Earrings</option>
          <option value="watch">Watch</option>
          <option value="loose_stone">Loose Stone</option>
          <option value="collection">Collection</option>
          <option value="other">Other</option>
        </Form.Select>
      </Col>

      <Col md={8}>
        <Form.Label htmlFor="desc">Description / Brand / Model</Form.Label>
        <Form.Control id="desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g., 1.2ct diamond solitaire ring in 14k gold" required {...vp("description", V.description)} />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="metal">Primary Metal (optional)</Form.Label>
        <Form.Control id="metal" value={primaryMetal} onChange={(e) => setPrimaryMetal(e.target.value)} placeholder="Gold / Platinum / Silver" {...vpOpt("primaryMetal", !!primaryMetal.trim())} />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="gems">Gemstones (optional)</Form.Label>
        <Form.Control id="gems" value={gemstones} onChange={(e) => setGemstones(e.target.value)} placeholder="e.g., Diamond, Sapphire" {...vpOpt("gemstones", !!gemstones.trim())} />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="value">Appraised / Replacement Value</Form.Label>
        <InputGroup hasValidation>
          <InputGroup.Text>$</InputGroup.Text>
          <Form.Control id="value" inputMode="numeric" value={appraisedValue} onChange={(e) => setAppraisedValue(e.target.value)} placeholder="e.g., 12000" required {...vp("appraisedValue", V.appraisedValue)} />
        </InputGroup>
      </Col>

      <Col md={4} className="pt-md-4">
        <div className="d-flex flex-wrap gap-3">
          <Form.Check id="haveAppraisal" type="checkbox" label="I have a written appraisal" checked={haveAppraisal} onChange={(e) => setHaveAppraisal(e.currentTarget.checked)} />
        </div>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="appraisalDate">Appraisal Date (optional)</Form.Label>
        <Form.Control id="appraisalDate" type="date" value={appraisalDate} onChange={(e) => setAppraisalDate(e.target.value)} {...vpOpt("appraisalDate", !!appraisalDate, V.appraisalDate)} />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="purchaseDate">Purchase Date (optional)</Form.Label>
        <Form.Control id="purchaseDate" type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} {...vpOpt("purchaseDate", !!purchaseDate, V.purchaseDate)} />
      </Col>
    </Row>
  );
};

/* ---------- Coverage & Security ---------- */
const SectionCoverageSecurity: React.FC<{
  values: { coverageType: CoverageType; scheduleItemCount: string; deductible: Deductible; storedInSafe: boolean; safeIsRated: boolean; monitoredAlarm: boolean; priorLosses5y: string; lastLossDate: string; notes: string; photoUrls: string };
  setters: { setCoverageType: (v: CoverageType) => void; setScheduleItemCount: (v: string) => void; setDeductible: (v: Deductible) => void; setStoredInSafe: (v: boolean) => void; setSafeIsRated: (v: boolean) => void; setMonitoredAlarm: (v: boolean) => void; setPriorLosses5y: (v: string) => void; setLastLossDate: (v: string) => void; setNotes: (v: string) => void; setPhotoUrls: (v: string) => void };
  V: any;
  vp: (name: string, pass: boolean) => any;
  vpOpt: (name: string, valuePresent: boolean, passIfPresent?: boolean) => any;
}> = ({ values, setters, V, vp, vpOpt }) => {
  const { coverageType, scheduleItemCount, deductible, storedInSafe, safeIsRated, monitoredAlarm, priorLosses5y, lastLossDate, notes, photoUrls } = values;
  const { setCoverageType, setScheduleItemCount, setDeductible, setStoredInSafe, setSafeIsRated, setMonitoredAlarm, setPriorLosses5y, setLastLossDate, setNotes, setPhotoUrls } = setters;

  return (
    <Row className="g-3">
      <Col md={4}>
        <Form.Label htmlFor="covType">Coverage Type</Form.Label>
        <Form.Select id="covType" value={coverageType} onChange={(e) => setCoverageType(e.target.value as CoverageType)} required {...vp("coverageType", V.coverageType)}>
          <option value="scheduled">Scheduled (itemized)</option>
          <option value="blanket">Blanket (collection)</option>
        </Form.Select>
      </Col>

      {coverageType === "scheduled" && (
        <Col md={4}>
          <Form.Label htmlFor="itemCount"># of Items to Schedule</Form.Label>
          <Form.Control id="itemCount" inputMode="numeric" value={scheduleItemCount} onChange={(e) => setScheduleItemCount(e.target.value)} placeholder="e.g., 2" {...vp("scheduleItemCount", V.scheduleItemCount)} />
        </Col>
      )}

      <Col md={4}>
        <Form.Label htmlFor="ded">Deductible</Form.Label>
        <Form.Select id="ded" value={deductible} onChange={(e) => setDeductible(e.target.value as Deductible)} required {...vp("deductible", V.deductible)}>
          <option value="">Select</option>
          <option value="0">$0</option>
          <option value="100">$100</option>
          <option value="250">$250</option>
          <option value="500">$500</option>
          <option value="1000">$1,000</option>
        </Form.Select>
      </Col>

      <Col md={12} className="pt-2">
        <Form.Label>Security</Form.Label>
        <div className="d-flex flex-wrap gap-3">
          <Form.Check id="safe" type="checkbox" label="Stored in a safe" checked={storedInSafe} onChange={(e) => setStoredInSafe(e.currentTarget.checked)} />
          <Form.Check id="ratedSafe" type="checkbox" label="Safe is UL-rated" checked={safeIsRated} onChange={(e) => setSafeIsRated(e.currentTarget.checked)} />
          <Form.Check id="alarm" type="checkbox" label="Monitored home alarm" checked={monitoredAlarm} onChange={(e) => setMonitoredAlarm(e.currentTarget.checked)} />
        </div>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="losses">Prior Losses in 5 Years</Form.Label>
        <Form.Control id="losses" inputMode="numeric" value={priorLosses5y} onChange={(e) => setPriorLosses5y(e.target.value)} placeholder="0" required {...vp("priorLosses5y", V.priorLosses5y)} />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="lastLoss">Last Loss Date (optional)</Form.Label>
        <Form.Control id="lastLoss" type="date" value={lastLossDate} onChange={(e) => setLastLossDate(e.target.value)} {...vpOpt("lastLossDate", !!lastLossDate, V.lastLossDate)} />
      </Col>

      <Col md={12}>
        <Form.Label htmlFor="notes">Notes (optional)</Form.Label>
        <Form.Control id="notes" as="textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Cut, color, clarity, serial numbers / certificates, etc." {...vpOpt("notes", !!notes.trim(), V.notes)} />
      </Col>

      <Col md={12}>
        <Form.Label htmlFor="photos">Photo / Doc URLs (optional)</Form.Label>
        <Form.Control id="photos" as="textarea" rows={2} value={photoUrls} onChange={(e) => setPhotoUrls(e.target.value)} placeholder="One URL per line (photos, appraisals, receipts)" {...vpOpt("photoUrls", !!photoUrls.trim(), V.photoUrls)} />
      </Col>
    </Row>
  );
};

/* ============================================================
   Confirmation block (Sectioned, readable, no JSON)
============================================================ */
const ConfirmationBlock: React.FC<{
  status: { ok: boolean; status?: number; id?: string; message?: string } | null;
  app: IJewelryLeadPayload | null;
}> = ({ status, app }) => {
  const ok = !!status?.ok;

  if (!app) {
    return (
      <Card className="border-0">
        <Card.Body className="p-4">
          <Alert variant="warning" className="mb-0">We don’t have your details to display yet.</Alert>
        </Card.Body>
      </Card>
    );
  }

  const titleize = (s: string) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <Card className="border-0">
      <Card.Body className="p-4">
        {ok ? (
          <Alert variant="success" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">Thank you! A licensed advisor will contact you shortly.</div>
              <div className="small">Submission status: <strong>{status?.status ?? 200}</strong>{status?.id ? <> • Reference ID: <strong>{status.id}</strong></> : null}</div>
            </div>
          </Alert>
        ) : (
          <Alert variant="warning" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">We couldn’t confirm your submission.</div>
              <div className="small">{status?.message || "Please try again or give us a call."}</div>
            </div>
          </Alert>
        )}

        <div className="mt-3">
          <Section title="Contact">
            <KV label="Name" value={dash(app.fullName)} />
            <KV label="Email" value={dash(app.email)} />
            <KV label="Phone" value={formatPhonePretty(app.phone)} />
          </Section>

          <Section title="Address">
            <KV label="Street" value={dash(app.address1)} />
            <KV label="Unit / Suite" value={dash(app.address2)} />
            <KV label="City / State / Zip" value={`${dash(app.city)}, ${dash(app.state)} ${dash(app.zipCode)}`} />
          </Section>

          <Section title="Jewelry Details">
            <KV label="Type" value={titleize(app.jewelryType)} />
            <KV label="Description" value={dash(app.description)} />
            <KV label="Primary Metal" value={dash(app.primaryMetal)} />
            <KV label="Gemstones" value={dash(app.gemstones)} />
            <KV label="Appraised Value" value={money(app.appraisedValue)} />
            <KV label="Have Appraisal" value={app.haveAppraisal ? "Yes" : "No"} />
            <KV label="Appraisal Date" value={dash(app.appraisalDate)} />
            <KV label="Purchase Date" value={dash(app.purchaseDate)} />
          </Section>

          <Section title="Coverage & Security">
            <KV label="Coverage Type" value={titleize(app.coverageType)} />
            <KV label="# Scheduled Items" value={dash(app.scheduleItemCount)} />
            <KV label="Deductible" value={app.deductible ? money(app.deductible) : "—"} />
            <KV label="Stored in Safe" value={app.storedInSafe ? "Yes" : "No"} />
            <KV label="UL-Rated Safe" value={app.safeIsRated ? "Yes" : "No"} />
            <KV label="Monitored Alarm" value={app.monitoredAlarm ? "Yes" : "No"} />
            <KV label="Prior Losses (5y)" value={dash(app.priorLosses5y)} />
            <KV label="Last Loss Date" value={dash(app.lastLossDate)} />
          </Section>

          <Section title="Notes / Files">
            <div className="p-3 bg-body-tertiary border rounded mb-2">{dash(app.notes)}</div>
            <KV label="Photo/Doc URLs" value={dash(app.photoUrls)} />
          </Section>
        </div>

        <div className="mt-4 d-flex flex-wrap gap-2">
          <Button href="tel:3033374433" variant="primary">Call {AGENT_PHONE_NUMBER}</Button>
          <Button variant="outline-secondary" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Back to top</Button>
          <Button variant="outline-secondary" onClick={() => window.print()}>Print</Button>
        </div>
      </Card.Body>
    </Card>
  );
};

/* ---------- Tiny helpers for confirmation layout ---------- */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-4">
    <h6 className="text-uppercase text-muted mb-2">{title}</h6>
    <Row as="dl" className="row-cols-1 row-cols-md-2 g-3 mb-0">{children}</Row>
    <hr className="mt-3 mb-0" />
  </div>
);

const KV: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Col className="mb-0">
    <dt className="small text-muted mb-1">{label}</dt>
    <dd className="mb-0">{value}</dd>
  </Col>
);

export default DISCOUNT_JEWELRY_Landing_v020_WIZARD;
